import { Button, Card, CardContent, Grid } from '@mui/material'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import useSandreList from 'utils/customHook/useSandreList'
import Input from '../../components/forms/Input'
import { FILTER_TYPE } from '../../administration/components/user/constants/UserConstants'
import Select from '../../components/forms/Select'
import SuperMultiAutocomplete from '../../components/forms/SuperMultiAutocomplete'
import { SANDRE } from '../../referencial/constants/ReferencialConstants'
import { i18nize } from '../../utils/StringUtil'
import { hasValue } from '../../utils/NumberUtil'
import { DEFAULT_OBJECTIVE_SUFFIX, OBJECTIVE_SUFFIX_BY_CAMPAIGN_TYPE, QUALITY_CAMPAIGN_TYPES } from '../constants/CampaignConstants'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import { authorizeContributors } from 'utils/HabilitationUtil'
import { shallowEqual, useSelector } from 'react-redux'
import CampaignDto from 'campaign/dto/CampaignDto'
import { includes } from 'lodash'
import { getCampaignContributorIds } from '../utils/CampaignUtils'

const FormFilterCampaigns = ({
    filter = {},
    onValidate = () => { },
    onReset = () => { },
    stationType,
    campaigns,
    isBookmarkable = false,
}) => {
    const campaignStatus = useSandreList('CAMPAGNES.STATUT')
    const campaignTypes = useSandreList(SANDRE.CAMPAGNES_TYPE)

    const [searchValue, setSearchValue] = useState(filter.searchValue)
    const [startDate, setStartDate] = useState(filter.startDate)
    const [endDate, setEndDate] = useState(filter.endDate)
    const [type, setType] = useState(filter.type)
    const [statut, setStatut] = useState(filter.statut)
    const [labo, setLabo] = useState(filter.labo)
    const [objectives, setObjectives] = useState(filter.objectives)
    const [bookmarksOnly, setBookmarksOnly] = useState(!!filter.bookmarksOnly)

    const objectiveSuffix = hasValue(type) ? OBJECTIVE_SUFFIX_BY_CAMPAIGN_TYPE[type] ?? DEFAULT_OBJECTIVE_SUFFIX : undefined
    const objectiveOptions = useSandreList(objectiveSuffix && `CAUSE_INTERVENTION.${objectiveSuffix}`)

    const {
        contributors,
        accountUser,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const onResetFilter = () => {
        onReset()
        setSearchValue(undefined)
        setStartDate(undefined)
        setEndDate(undefined)
        setType(undefined)
        setStatut(undefined)
        setLabo(undefined)
        setObjectives(undefined)
        setBookmarksOnly(false)
    }

    const onValidateFilter = () => {
        onValidate({
            searchValue,
            startDate,
            endDate,
            type,
            statut,
            labo,
            objectives,
            bookmarksOnly,
        })
    }

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={3}>
                        <Input
                            title={i18n.search}
                            value={searchValue}
                            onChange={setSearchValue}
                        />
                    </Grid>
                    <Grid size={3}>
                        <SimpleDatePicker
                            label={i18n.startDate}
                            value={startDate}
                            onChange={setStartDate}
                        />
                    </Grid>
                    <Grid size={3}>
                        <SimpleDatePicker
                            label={i18n.endDate}
                            value={endDate}
                            onChange={setEndDate}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            className={'clear-left'}
                            label={i18n.status}
                            options={campaignStatus}
                            value={statut}
                            onChange={setStatut}
                            keyValue={'code'}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.type}
                            value={type}
                            options={stationType === STATION_TYPE_NAME.quality ? i18nize(QUALITY_CAMPAIGN_TYPES) : campaignTypes}
                            onChange={v => {
                                setType(v)
                                setObjectives(undefined)
                            }}
                            keyValue={'code'}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={3}>
                        <SuperMultiAutocomplete
                            multiple
                            label={i18n.objectives}
                            options={objectiveOptions}
                            values={objectives}
                            onChange={setObjectives}
                            keyLabel='name'
                            keyValue='code'
                            limit={2}
                        />
                    </Grid>
                    <Grid size={3}>
                        <MultiContributorsAutocomplete
                            label={i18n.contributors}
                            options={authorizeContributors(contributors, accountUser, parseInt(labo))
                                .filter(contributor => includes(campaigns.flatMap(getCampaignContributorIds), parseInt(contributor.id)))
                                .map(c => ({ ...c, name: c.mnemonique ?? c.name }))}
                            onChange={id => setLabo(id)}
                            values={parseInt(labo)}
                            displayWithCode
                        />
                    </Grid>
                    {isBookmarkable && (
                        <Grid size={3}>
                            <Select
                                label={i18n.filter}
                                options={[{ code: FILTER_TYPE.BOOKMARK, name: i18n.bookmarks }]}
                                value={bookmarksOnly ? FILTER_TYPE.BOOKMARK : undefined}
                                onChange={v => setBookmarksOnly(v === FILTER_TYPE.BOOKMARK)}
                                keyValue='code'
                                nullLabel='&nbsp;'
                            />
                        </Grid>
                    )}
                    <Grid container size={isBookmarkable ? 12 : 3} justifyContent='flex-end' spacing={1} alignItems='center'>
                        <Button variant='outlined' onClick={onResetFilter}>
                            {i18n.reinit}
                        </Button>
                        <Button variant='contained' onClick={onValidateFilter}>
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FormFilterCampaigns.propTypes = {
    filter: PropTypes.exact({
        searchValue: PropTypes.string,
        startDate: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        endDate: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        type: PropTypes.number,
        statut: PropTypes.number,
        labo: PropTypes.number,
        objectives: PropTypes.arrayOf(PropTypes.number),
        bookmarksOnly: PropTypes.bool,
    }),
    isBookmarkable: PropTypes.bool,
    sortBy: PropTypes.number,
    onChange: PropTypes.func,
    onValidate: PropTypes.func,
    onReset: PropTypes.func,
    stationType: PropTypes.string,
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(CampaignDto)),
}

export default FormFilterCampaigns